import { useState, useMemo } from "react";

// Versión simplificada del hook. La versión anterior usaba un useReducer con
// múltiples useEffect en cascada que tenía dos bugs:
//   1) `withError` se computaba con `state.errors` viejo (closure) en lugar de
//      `action.validationErrors` del dispatch.
//   2) El estado visual no consideraba `touched`, así que el borde rojo aparecía
//      en todos los fields con error de validación aunque el usuario nunca los
//      hubiera tocado (visible con `validateOnChange=true`).
//
// Esta versión calcula `classes` directamente desde las props con useMemo:
//   - withError = errors && touched (convención Formik para mostrar errores)
//   - El resto de las clases (active/disabled/etc.) sigue la misma convención
//     que la versión anterior.
//
// Compatibilidad: el shape del return se mantiene; los únicos consumers del
// hook (los componentes de FormikForm/) solo usan `classes` y `setActive`.

interface States {
  withError: boolean;
  withContent: boolean;
  isActive: boolean;
  isDisabled: boolean;
  isLoading: boolean;
}

interface FieldValidationState {
  classes: string;
  fieldStates: States;
  errors: any;
  value: string | number;
  name: string;
}

export const useFormikFieldValidation = (
  errors,
  name,
  disabled,
  touched: any = true
): FieldValidationState & { setValue: Function; setActive: Function } => {
  const [isActive, setIsActive] = useState(false);

  const hasVisibleError = Boolean(errors && touched);

  const classes = useMemo(() => {
    const activeClass = isActive ? "field-active" : "field-inactive";
    const disabledClass = disabled ? "field-disabled" : "field-enabled";
    const errorClass = hasVisibleError
      ? `with-error ${name}-with-error`
      : `without-error ${name}-without-error`;
    // contentClass/loadingClass quedan estáticos — los consumers actuales no
    // dependen de esos valores dinámicamente.
    const contentClass = "without-content";
    const loadingClass = "";
    return `${contentClass} ${activeClass} ${loadingClass} ${disabledClass} ${errorClass}`;
  }, [hasVisibleError, isActive, disabled, name]);

  return {
    classes,
    setActive: setIsActive,
    fieldStates: {
      withError: hasVisibleError,
      withContent: false,
      isActive,
      isDisabled: Boolean(disabled),
      isLoading: false,
    },
    errors,
    value: "",
    name: name || "",
    setValue: () => {},
  };
};
